import { call, put, putResolve, select, take } from 'redux-saga/effects';
import * as appActions from '../../actions';
import { copyObject } from '../../utility';
import {
  getActiveClient,
  getActiveContact,
  getActiveFacility,
  getActiveProject,
  getActiveAddress,
  getNav,
  getTags,
} from '../../reducers/selectors';
import { projectsApi } from '../../../services/projects/projectsApi';
import { enqueueSnackbar } from 'notistack';
import { t } from '@lingui/core/macro';

export function* watchSetAddProjectFlow() {
  while (true) {
    const { payload: flow } = yield take(appActions.SET_ADD_PROJECT_FLOW);

    if (flow.flowClient) {
      let copy = copyObject(flow);
      // Check for client save
      yield put({ type: appActions.SET_APP_STATE, payload: { saving: true } });
      const client = yield select(getActiveClient);
      const project = yield select(getActiveProject);

      const { data: resultClient } = yield putResolve(
        projectsApi.endpoints.createClient.initiate(client)
      );

      yield put({ type: appActions.SET_ACTIVE_CLIENT, payload: resultClient });

      let projectCopy = copyObject(project);
      projectCopy.client_id = resultClient.id;
      yield put({ type: appActions.SET_ACTIVE_PROJECT, payload: projectCopy });

      if (flow.saveOwner) {
        const clientOwner = {
          client_id: flow.baseClientId,
          target_client_id: resultClient.id,
          user_id: flow.ownerId,
        };
        yield put(projectsApi.endpoints.createClientOwner.initiate(clientOwner));
      }
      // Check for tags
      if (flow.tags) {
        const tags = yield select(getTags);
        for (let i = 0; i < flow.tags.length; i++) {
          const value = tags.tag_values.find((v) => v.id === flow.tags[i]);
          let obj = {
            node_type: 'client',
            node_id: resultClient.id,
            tag_value_id: value.id,
            tag_type_id: value.tag_type_id,
          };

          yield put(projectsApi.endpoints.createTag.initiate(obj));
        }
      }

      copy.flowClient = false;

      yield put({ type: appActions.SET_ADD_PROJECT_FLOW, payload: copy });
      yield put({ type: appActions.SET_APP_STATE, payload: { saving: false } });

      return;
    } else if (flow.flowProjectContact) {
      let copy = copyObject(flow);
      yield put({ type: appActions.SET_APP_STATE, payload: { saving: true } });

      const contact = yield select(getActiveContact);
      const project = yield select(getActiveProject);
      const { data: resultContact } = yield putResolve(
        projectsApi.endpoints.createContact.initiate(contact)
      );

      let projectCopy = copyObject(project);

      projectCopy.project_data.project_contact_ids = [resultContact.id];
      yield put({ type: appActions.SET_ACTIVE_PROJECT, payload: projectCopy });

      copy.flowProjectContact = false;
      yield put({ type: appActions.SET_ADD_PROJECT_FLOW, payload: copy });
      yield put({ type: appActions.SET_APP_STATE, payload: { saving: false } });
      return;
    } else if (flow.flowProjectFacility) {
      let copy = copyObject(flow);
      yield put({ type: appActions.SET_APP_STATE, payload: { saving: true } });

      const facility = yield select(getActiveFacility);
      const project = yield select(getActiveProject);
      const address = yield select(getActiveAddress);

      const { data: resultAddress } = yield putResolve(
        projectsApi.endpoints.createFacilityAddress.initiate(address)
      );
      let facilityCopy = copyObject(facility);
      facilityCopy.address_id = resultAddress.id;

      const { data: resultFacility } = yield putResolve(
        projectsApi.endpoints.createFacility.initiate(facilityCopy)
      );

      let projectCopy = copyObject(project);
      projectCopy.facility_id = resultFacility.id;
      yield put({ type: appActions.SET_ACTIVE_PROJECT, payload: projectCopy });

      copy.flowProjectFacility = false;
      yield put({ type: appActions.SET_ADD_PROJECT_FLOW, payload: copy });
      yield put({ type: appActions.SET_APP_STATE, payload: { saving: false } });
      return;
    } else if (flow.flowFacilityContact) {
      let copy = copyObject(flow);
      yield put({ type: appActions.SET_APP_STATE, payload: { saving: true } });

      const contact = yield select(getActiveContact);
      const project = yield select(getActiveProject);

      const { data: resultContact } = yield putResolve(
        projectsApi.endpoints.createContact.initiate(contact)
      );

      let projectCopy = copyObject(project);
      projectCopy.project_data.facility_contact_ids.push(resultContact.id);
      yield put({ type: appActions.SET_ACTIVE_PROJECT, payload: projectCopy });

      copy.flowFacilityContact = false;
      yield put({ type: appActions.SET_ADD_PROJECT_FLOW, payload: copy });
      yield put({ type: appActions.SET_APP_STATE, payload: { saving: false } });
      return;
    } else if (flow.flowProjectSaveAndNavigate) {
      yield call(saveProjectAndWorkflow, flow);
    } else if (flow.flowCreateWorkflowAndNavigate) {
      let copy = copyObject(flow);
      yield put({ type: appActions.SET_APP_STATE, payload: { saving: true } });

      const { data: resultWorkflow } = yield putResolve(
        projectsApi.endpoints.createWorkflow.initiate({
          projectId: copy.workflowParams.projectId,
          workflowType: copy.workflowParams.workflowType,
          workflowData: copy.workflowParams.workflowData,
        })
      );

      yield put({ type: appActions.SET_APP_STATE, payload: { saving: false } });
      const nav = yield select(getNav);
      let newNav = {
        //drawerOpen: nav.drawerOpen,
      };

      const navIndex =
        flow.workflowParams.workflowType === 'measure' ||
        flow.workflowParams.workflowType === 'model'
          ? 'measureModel'
          : 'monitor';

      newNav.index = navIndex;
      newNav.activeNavId = navIndex;
      newNav.projectId = resultWorkflow.project_id;

      yield put({
        type: appActions.SET_ADD_PROJECT_FLOW,
        payload: { inProgress: false },
      });

      //copyNav.drawerOpen = false;
      yield put({ type: appActions.SET_NAV, payload: newNav });
    }
  }
}

export function* saveProjectAndWorkflow(flow) {
  yield put({ type: appActions.SET_APP_STATE, payload: { saving: true } });
  let project = yield select(getActiveProject);
  try {
    // Keep the saved project on failure so retrying only creates the workflow.
    if (!project.id || project.id === 'new') {
      const result = yield putResolve(projectsApi.endpoints.createProject.initiate(project));
      if (result.error || !result.data?.id) {
        throw result.error || new Error('Project creation returned no project ID');
      }
      project = result.data;
      yield put({ type: appActions.SET_ACTIVE_PROJECT, payload: project });
    }

    const result = yield putResolve(projectsApi.endpoints.createWorkflow.initiate({
      projectId: project.id,
      workflowType: flow.workflowType,
      workflowData: {},
    }));
    if (result.error || !result.data?.id) {
      throw result.error || new Error('Workflow creation returned no workflow ID');
    }

    const navIndex = flow.workflowType === 'monitor' ? 'monitor' : 'measureModel';
    yield put({
      type: appActions.SET_ADD_PROJECT_FLOW,
      payload: { inProgress: false, projectId: project.id, workflowType: flow.workflowType },
    });
    yield put({
      type: appActions.SET_NAV,
      payload: { index: navIndex, activeNavId: navIndex, projectId: project.id },
    });
  } catch (error) {
    console.error('Error creating project workflow:', error);
    enqueueSnackbar(
      project.id && project.id !== 'new'
        ? t`The project was created, but the workflow could not be created. Try again to complete it.`
        : t`The project could not be created. Please try again.`,
      { variant: 'error' }
    );
  } finally {
    yield put({ type: appActions.SET_APP_STATE, payload: { saving: false } });
  }
}
